import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import cn from "classnames";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import { GlobalNavigationProps } from "../GlobalNavigation";
import Link from "@/components/Link";
import { CaretDownSolid } from "@/icons";
import { getHrefFromCustomUrlPlugin } from "@/helpers";
import HeaderNavigationPanel from "./HeaderNavigationPanel";
import { ComponentProps } from "react";

export type SuperHeaderProps = Pick<GlobalNavigationProps, "menu">;

const getLastUnit = (title: string): { rest: string; lastUnit: string } => {
  const words = title.split(" ");
  let lastUnit = words.pop() ?? "";
  if (words[words.length - 1] === "&") {
    words.pop();
    lastUnit = `& ${lastUnit}`;
  }
  return { rest: words.join(" "), lastUnit };
};

const renderMenuText = (title: string, showCaret: boolean) => {
  const { rest, lastUnit } = getLastUnit(title);

  return (
    <span className="block text-left lg:max-[1067px]:w-min">
      {rest && `${rest} `}
      {showCaret ? (
        <span className="whitespace-nowrap">
          {lastUnit}
          {"\u00A0"}
          <CaretDownSolid
            className="text-swdc-black-600 group-active:text-swdc-blue-500 inline-block w-3.5 shrink-0 align-middle"
            aria-hidden="true"
          />
        </span>
      ) : (
        lastUnit
      )}
    </span>
  );
};

const navItemClass =
  "group after:bg-swdc-orange-400 text-subhead-2xs text-swdc-black-600 active:text-swdc-blue-500 after:rounded-corner-pill relative block text-center py-2.5 font-bold outline-none after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0 after:content-[''] hover:after:h-0.5 focus:after:h-0.5 active:after:h-0.5";

const Header: React.FC<SuperHeaderProps> = ({ menu }) => {
  const isFiveLinkVariant = menu?.items && menu?.items?.length === 5;

  return (
    <div className="h-25.5 bg-white">
      <nav
        className="swdc-container mx-auto flex h-full w-full items-center justify-between pt-6 pb-5"
        aria-label="Secondary"
      >
        {menu?.logo}

        <div className="ml-4 flex min-w-0 flex-1 items-center justify-end lg:ml-2 xl:ml-8">
          <PopoverGroup
            as="ul"
            className={cn(
              "flex min-w-0 shrink items-start",
              isFiveLinkVariant
                ? "mr-6 gap-4 xl:gap-8"
                : "mr-12 gap-6 lg:gap-12",
            )}
          >
            {menu?.items?.map((item, index) => {
              return (
                <li key={`${item.title}-${index}`}>
                  {item.href && item.title ? (
                    <Link
                      className={navItemClass}
                      href={getHrefFromCustomUrlPlugin(item.href)}
                      {...getDataLinkAttributes({
                        dlt: item.dlt,
                        dld: item.dld,
                      })}
                      {...getDataAutomationId(`navigation-dropdown-${index}`)}
                    >
                      {renderMenuText(item.title, false)}
                    </Link>
                  ) : (
                    <Popover>
                      <PopoverButton
                        className={({ open }) =>
                          cn(
                            navItemClass,
                            open &&
                              "after:h-1! hover:after:h-0.5! [&_svg]:rotate-180",
                          )
                        }
                        {...getDataLinkAttributes({
                          dld: item.dld,
                          dlt: item.dlt,
                        })}
                        {...getDataAutomationId(`navigation-dropdown-${index}`)}
                      >
                        {renderMenuText(item.title ?? "", true)}
                      </PopoverButton>
                      <PopoverBackdrop className="bg-swdc-cool-gray-200/30 fixed inset-0 -z-10 backdrop-blur" />
                      <PopoverPanel
                        className="absolute left-1/2 z-999 mt-7.5 w-full max-w-308 -translate-x-1/2"
                        unmount={false}
                      >
                        {({ close }) => (
                          <HeaderNavigationPanel
                            {...item}
                            panelColor={
                              {
                                coolGray: "cool-gray-700",
                                blue: "blue-500",
                                gray: "gray-500",
                              }[menu.colorVariant] as ComponentProps<
                                typeof HeaderNavigationPanel
                              >["panelColor"]
                            }
                            index={index}
                            closeMenu={close}
                          />
                        )}
                      </PopoverPanel>
                    </Popover>
                  )}
                </li>
              );
            })}
          </PopoverGroup>
          {menu.cta}
        </div>
      </nav>
    </div>
  );
};

export default Header;
